import { capitalize, cloneDeep, get, isEmpty, omit, sortBy } from 'lodash';
import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import {
  closeGenericModal,
  openGenericModal,
  openSmallModal,
  refreshGenericModal,
} from '@actions/modal';
import { showConfirmationMessage, showErrorMessage } from '@actions/messageconfirmation';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { formatNumberToExcelUsage, getNumberFormattedWithDecimals } from '@commons/DisplayNumber';
import { getAnalyticsFoodCostByCategoryExportByStoreModalConfig } from './utils/exportUtils';
import {
  getClientStoreNameTranslation,
  getFormattedCurrencyName,
} from '@commons/utils/translations';
import { getFilteredStoresWithSelectedGroups } from '@commons/ExportModalWithStoresAndPeriod/utils';
import { getUniqueGroupsOfStores } from '@commons/utils/groups';
import { getUserTimezone } from '@commons/utils/date';
import { METRIC_KEY } from '@commons/constants/metricKey';
import { NestedList } from '@commons/utils/styledLibraryComponents';
import EmptyState from '@commons/EmptyState';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';
import StoresWithActiveProperty from '@commons/DropdownItems/StoresWithActiveProperty';

import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

import { getEntityUnit } from '@stocks/utils';

import {
  computeAndFormatTotalMetrics,
  fetchAnalytics,
  fetchInventoryDateOfStoreByDates,
  fetchLocationsByAccountId,
} from './utils/fetch';
import {
  Container,
  ContentContainer,
  FilterContainer,
  HeaderContainer,
  ListContainer,
} from './styledComponents';
import { fetchGroupsByStoreIds } from '../utils/fetches';
import { getCustomFilterWithNonSalesConsumption } from '@commons/utils/filtersFetches';
import { getTableViewColumns } from './utils/columns';
import { OrderAnalyticsByCategoryByStoreExportModal } from './components/OrderAnalyticsCategoryStoreExportModal';
import { OrderAnalyticsByCategoryExportModal } from './components/OrderAnalyticsCategoryExportModal';
import DeepsightFiltersButton from './components/Headers';

const ORDER_BY_KEY = 'name';

export const OrdersAnalyticsCategory = (props) => {
  const {
    user,
    currency,
    client: { hasMultipleBrands, storeName, defaultTimezone },
    match: { path },
    stores,
    showMessage,
    showErrorMessage,
    clientBrands,
    clientRetailers,
    openGenericModal,
    refreshGenericModal,
    closeGenericModal,
  } = props;

  const userTimezone = getUserTimezone();
  const yesterday = moment.tz(userTimezone).subtract(1, 'day');

  const METRICS = [
    {
      key: METRIC_KEY.TURNOVER,
      name: capitalize(getFormattedCurrencyName(currency.shortenedName, true)),
    },
    { key: METRIC_KEY.UNIT, name: i18next.t('GENERAL.UNIT') },
  ];

  /**
   * FILTERS STATES
   */
  const [locations, setLocations] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);

  const [selectedBrands, setSelectedBrands] = useState([]);

  const [selectedRetailers, setSelectedRetailers] = useState([]);

  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const [formattedStores] = useState(
    stores.map((store) => ({
      ...store,
      renderValue: () => StoresWithActiveProperty(store),
    })),
  );
  const [selectedStores, setSelectedStores] = useState([]);

  const [applyFilters, setApplyFilters] = useState(true);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [filtersOrderAnalytics, setFiltersOrderAnalytics] = useState(null);
  const [withNonSalesConsumption, setWithNonSalesConsumption] = useState(true);
  const customFilterWithNonSalesConsumption = getCustomFilterWithNonSalesConsumption(
    withNonSalesConsumption,
    setWithNonSalesConsumption,
  );

  const [startDate, setStartDate] = useState(moment.tz(userTimezone).subtract(1, 'month'));
  const [endDate, setEndDate] = useState(yesterday);
  const [focusedDateInput, setFocusedDateInput] = useState(null);

  const [startMonth, setStartMonth] = useState(
    moment
      .tz(userTimezone)
      .subtract(1, 'month')
      .startOf('month')
      .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
  );
  const [endMonth, setEndMonth] = useState(
    moment.tz(userTimezone).endOf('month').format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
  );

  /**
   * DATA STATES
   */
  const [activeData, setActiveData] = useState({});
  const [analytics, setAnalyticsData] = useState([]);

  const [turnOver, setTurnover] = useState(0);
  const [turnoverByStoreId, setTurnoverByStoreId] = useState({});
  const [totalLossByStoreId, setTotalLossByStoreId] = useState({});

  const [lossRate, setLossRate] = useState(0);

  const [formattedInventoryStockDay, setFormattedInventoryStockDay] = useState({});
  const [selectedMetric, setSelectedMetric] = useState(METRICS[0]);

  /**
   * DISPLAY STATES
   */
  const [headers, setHeaders] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [renderEmptyState, setRenderEmptyState] = useState(false);
  const [isRetrievingAnalytics, setIsRetrievingAnalytics] = useState(false);
  const [totalRow, setTotalRow] = useState({});

  /**
   * Export modal states
   */
  const [exportStores, setExportStores] = useState(stores);
  const [exportSelectedStores, setExportSelectedStores] = useState(null);
  const [exportGroups, setExportGroups] = useState([]);
  const [exportSelectedGroups, setExportSelectedGroups] = useState([]);
  const [exportPeriod, setExportPeriod] = useState(null);

  const columnsFilterList = headers.filter((column) => column.filterType === 'numeric');

  // Handle the fetch of locations and groups
  useEffect(() => {
    if (!stores || !stores.length) {
      return;
    }

    setHeaders(getTableViewColumns(currency, startDate, endDate, selectedMetric.key));

    try {
      (async function loadData() {
        const storeIds = stores.map(({ id }) => id);

        const [fetchedLocations, fetchedGroups] = await Promise.all([
          fetchLocationsByAccountId(user.id, showMessage),
          fetchGroupsByStoreIds(storeIds, showErrorMessage),
        ]);

        setLocations(fetchedLocations);
        setGroups(fetchedGroups);

        const activeStores = formattedStores.filter(({ active }) => active);
        setSelectedStores(activeStores);

        // Initialize export modal states
        const uniqGroups = getUniqueGroupsOfStores(activeStores);

        setExportGroups(uniqGroups);
        setExportSelectedGroups(uniqGroups);

        setIsLoading(false);
      })();
    } catch {
      showErrorMessage(i18next.t('ORDERS.BY_CATEGORY.FETCH_DATA_FAILURE'));
    }
  }, []);

  useEffect(() => {
    if (!exportSelectedGroups.length) {
      return;
    }

    const filteredStores = getFilteredStoresWithSelectedGroups(exportSelectedGroups, stores);

    // Initial list of stores to export
    setExportStores(filteredStores);

    // Selected stores to export
    setExportSelectedStores(filteredStores);
  }, [exportSelectedGroups]);

  // Handle the metrics toggle switch
  useEffect(() => {
    if (isEmpty(activeData)) {
      return;
    }

    const formattedActiveData = {
      ...activeData,
      [selectedMetric.key]: formatMetricData(analytics, selectedMetric.key),
    };

    setActiveData(formattedActiveData);

    setHeaders(getTableViewColumns(currency, startDate, endDate, selectedMetric.key));
  }, [selectedMetric]);

  // Handle the fetch of the analytics
  useEffect(() => {
    const storeIds = selectedStores.map((store) => store.id);

    if (isRetrievingAnalytics || !storeIds.length || !startDate || !endDate) {
      return;
    }

    setHeaders(getTableViewColumns(currency, startDate, endDate, selectedMetric.key));

    setRenderEmptyState(false);
    setIsRetrievingAnalytics(true);

    (async function loadData() {
      try {
        const result = await fetchAnalytics(storeIds, startDate, endDate, METRICS, {
          withNonSalesConsumption,
        });

        setTotalRow(result.total);
        setAnalyticsData(result.data);

        setTotalLossByStoreId(result.lossByStoreId);
        setTurnoverByStoreId(result.turnoverByStoreId);

        const totalTurnover = result.turnoverByStoreId.total;

        setTurnover(getNumberFormattedWithDecimals(totalTurnover, 0));

        if (totalTurnover) {
          const computedLossRate = (result.total[METRIC_KEY.TURNOVER].loss * 100) / totalTurnover;

          setLossRate(getNumberFormattedWithDecimals(computedLossRate, 2));
        }
      } catch {
        showErrorMessage(i18next.t('ORDERS.BY_CATEGORY.FETCH_DATA_FAILURE'));

        setLossRate(0);
        setTurnover(0);

        setRenderEmptyState(true);
      } finally {
        setIsRetrievingAnalytics(false);
      }
    })();
  }, [selectedStores, startDate, endDate, withNonSalesConsumption]);

  // Apply filters on retrieved analytics
  useEffect(() => {
    if (isEmpty(analytics)) {
      return;
    }

    if ((!advancedFilters || !advancedFilters.length) && applyFilters) {
      const formattedActiveData = {
        ...analytics,
        [selectedMetric.key]: formatMetricData(analytics, selectedMetric.key),
      };

      setActiveData(formattedActiveData);

      const formattedLossRate = turnoverByStoreId['total']
        ? getNumberFormattedWithDecimals(
            (totalRow[METRIC_KEY.TURNOVER].loss * 100) / turnoverByStoreId['total'],
            2,
          )
        : 0;

      setLossRate(formattedLossRate);
      return;
    }

    if (!applyFilters) {
      return;
    }

    const filteredData = METRICS.reduce((acc, { key }) => {
      acc[key] = formatMetricData(analytics, key);
      return acc;
    }, {});

    setActiveData(filteredData);

    // Transform filtered data to category-keyed structure for computeAndFormatTotalMetrics method
    const filteredDataByCategoryForTotal = formatCategoryData(filteredData);

    const formattedTotalRow = computeAndFormatTotalMetrics(
      filteredDataByCategoryForTotal,
      turnoverByStoreId['total'],
      METRICS,
    );

    setTotalRow(formattedTotalRow);

    const formattedLossRate = turnoverByStoreId['total']
      ? getNumberFormattedWithDecimals(
          (totalRow[METRIC_KEY.TURNOVER].loss * 100) / turnoverByStoreId['total'],
          2,
        )
      : 0;
    setLossRate(formattedLossRate);
  }, [advancedFilters, analytics, applyFilters, selectedMetric]);

  // Retrieve inventory dates when single store being selected
  useEffect(() => {
    if (selectedStores.length !== 1) {
      return;
    }

    const storeIds = selectedStores.map(({ id }) => id);

    try {
      (async function loadData() {
        const formattedDate = await fetchInventoryDateOfStoreByDates(
          storeIds,
          startMonth,
          endMonth,
          userTimezone,
          showMessage,
        );

        setFormattedInventoryStockDay(formattedDate);
      })();
    } catch {
      showErrorMessage(i18next.t('ORDERS.BY_CATEGORY.FETCH_DATA_FAILURE'));
    }
  }, [selectedStores, startMonth, endMonth]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setRenderEmptyState(isEmpty(activeData));
    setIsRetrievingAnalytics(false);
  }, [activeData]);

  useEffect(() => {
    if (!exportSelectedStores) {
      return;
    }

    handleFoodCostByCategoryExportByStoreModal(true);
  }, [exportStores, exportSelectedStores, exportPeriod]);

  const handleSelectedDates = (dates) => {
    if (!moment.tz(dates.startDate, userTimezone).isSame(startDate, 'day')) {
      setEndDate(null);
      setStartDate(dates.startDate);
    }

    if (!moment.tz(dates.endDate, userTimezone).isSame(endDate, 'day')) {
      setEndDate(dates.endDate);
    }
  };

  const formatMetricData = (data, key) =>
    data[key].reduce((result, categoryData) => {
      const filteredCategory = cloneDeep(categoryData);

      const childItems = (advancedFilters || []).reduce(
        (filteredChildItems, { propertyKey, doFilter, value }) =>
          doFilter(filteredChildItems, `${propertyKey}`, value),
        categoryData.childItems,
      );

      // If client has translations module, we do not translate category.name = null
      // If client does not have translations module, categories are not translated. In both cases, we need to handle "category.name = null" case
      filteredCategory.name =
        filteredCategory.name == null || filteredCategory.name == 'null'
          ? i18next.t('GENERAL.OTHER').toUpperCase()
          : filteredCategory.name;

      filteredCategory.childItems = childItems;

      filteredCategory.nestedData = childItems.map((ingredient) => ({
        ...ingredient,
        link: `/admin/products/ingredients/${ingredient.id}/details`,
      }));

      if (childItems.length) {
        // Recalculate only the summable properties, preserve calculated ones
        const recalculatedStats = {
          foodCost: {
            order: null,
            rushOrder: null,
            orderTotal: null,
            transferIn: null,
            transferOut: null,
            transferTotal: null,
            finalStock: null,
            initialStock: null,
            stockVariation: null,
            realConsumption: null,
            gapFromTheoretical: null,
          },
          loss: null,
          inventoryShrinkage: null,
          theoreticalConsumption: null,
        };

        // Sum up summable stats from filtered childItems
        childItems.forEach((childItem) => {
          // Sum foodCost properties
          if (childItem.foodCost) {
            Object.keys(recalculatedStats.foodCost).forEach((property) => {
              if (childItem.foodCost[property] != null) {
                recalculatedStats.foodCost[property] =
                  (recalculatedStats.foodCost[property] || 0) + childItem.foodCost[property];
              }
            });

            recalculatedStats.foodCost.hasStockVariationRealValue =
              recalculatedStats.foodCost?.hasStockVariationRealValue ||
              childItem.foodCost.hasStockVariationRealValue;
            recalculatedStats.foodCost.hasInitialStockRealValue =
              recalculatedStats.foodCost?.hasInitialStockRealValue ||
              childItem.foodCost.hasInitialStockRealValue;
            recalculatedStats.foodCost.hasFinalStockRealValue =
              recalculatedStats.foodCost?.hasFinalStockRealValue ||
              childItem.foodCost.hasFinalStockRealValue;
          }

          // Sum other summable properties
          if (childItem.loss != null) {
            recalculatedStats.loss = (recalculatedStats.loss || 0) + childItem.loss;
          }

          if (childItem.inventoryShrinkage != null) {
            recalculatedStats.inventoryShrinkage =
              (recalculatedStats.inventoryShrinkage || 0) + childItem.inventoryShrinkage;
          }

          if (childItem.theoreticalConsumption != null) {
            recalculatedStats.theoreticalConsumption =
              (recalculatedStats.theoreticalConsumption || 0) + childItem.theoreticalConsumption;
          }
        });

        // Preserve the original calculated values (realOverTurnover, relativeRealFromTheorical)
        const aggregatedCategory = {
          ...omit(filteredCategory, [
            'nestedContent',
            'foodCost',
            'loss',
            'inventoryShrinkage',
            'theoreticalConsumption',
          ]),
          ...recalculatedStats,
          // Keep the original calculated values because they are not summable
          realOverTurnover: filteredCategory.realOverTurnover,
          relativeRealFromTheorical: filteredCategory.relativeRealFromTheorical,
        };

        result.push(aggregatedCategory);
      }

      return result;
    }, []);

  // Reverse function of formatMetricData: transform data from metric-keyed structure to category-keyed structure
  const formatCategoryData = (filteredData) => {
    const dataByCategory = {};

    // Iterate through each metric (turnover, unit)
    Object.keys(filteredData).forEach((metricKey) => {
      // Iterate through each category in the metric
      filteredData[metricKey].forEach((categoryData) => {
        const categoryName = categoryData.name;

        if (!dataByCategory[categoryName]) {
          dataByCategory[categoryName] = {
            stats: {
              [METRIC_KEY.TURNOVER]: {},
              [METRIC_KEY.UNIT]: {},
            },
          };
        }

        // Extract the stats for this metric and category
        const categoryStats = {
          foodCost: categoryData.foodCost,
          loss: categoryData.loss,
          inventoryShrinkage: categoryData.inventoryShrinkage,
          theoreticalConsumption: categoryData.theoreticalConsumption,
          realOverTurnover: categoryData.realOverTurnover,
          relativeRealFromTheorical: categoryData.relativeRealFromTheorical,
          // Add any other properties that might be present
          ...omit(categoryData, ['name', 'childItems', 'nestedData', 'nestedContent']),
        };

        dataByCategory[categoryName].stats[metricKey] = categoryStats;
      });
    });

    return dataByCategory;
  };

  // Trigger the export of actual nested list data
  const triggerExport = () => {
    const data = METRICS.reduce(
      (acc, metric) => {
        activeData[metric.key].forEach((element) => {
          const childItems = get(element, 'childItems', null);
          const category = get(element, 'name', null);

          if (childItems) {
            childItems.forEach((entity) => {
              const result = {
                ...entity,
                category,
                unit: getEntityUnit(entity.unit, false),
              };

              if (metric.key === METRIC_KEY.TURNOVER) {
                acc.turnoverSheetData.push(result);
              }

              if (metric.key === METRIC_KEY.UNIT) {
                acc.unitSheetData.push(result);
              }
            });
          }
        });

        return acc;
      },
      { turnoverSheetData: [], unitSheetData: [] },
    );

    const contextSheetData = [];
    selectedStores.forEach((store) => {
      const turnover = get(turnoverByStoreId, `[${store.id}].value`, null);

      const lossRate = formatNumberToExcelUsage(
        (get(totalLossByStoreId, `${store.id}.total`, null) / turnover) * 100,
        2,
      );

      contextSheetData.push({
        storeName: store.name,
        startDate,
        endDate,
        turnover: get(turnoverByStoreId, `[${store.id}].value`),
        lossRate: isNaN(lossRate) || !turnover ? '' : `${lossRate} %`,
      });
    });

    props.openModalExportInfo({
      component: OrderAnalyticsByCategoryExportModal,
      title: i18next.t('ORDERS.BY_CATEGORY.EXPORT_IN_PROGRESS'),
      turnoverSheetData: sortBy(data.turnoverSheetData, ['category', 'name']),
      unitSheetData: sortBy(data.unitSheetData, ['category', 'name']),
      contextSheetData: sortBy(contextSheetData, 'storeName'),
      currency,
      showMessage,
    });
  };

  // Open the export modal for food cost by category export by store
  const handleFoodCostByCategoryExportByStoreModal = (shouldRefreshModal = false) => {
    const params = getAnalyticsFoodCostByCategoryExportByStoreModalConfig(
      {
        maxFutureDate: yesterday,
        initialStartDate: startDate,
        initialEndDate: endDate,
        exportGroups,
        exportSelectedGroups,
        setExportSelectedGroups,
        exportModalStores: exportStores,
        exportSelectedStores,
        setExportSelectedStores,
        setExportPeriod,
        clientStoreName: getClientStoreNameTranslation(storeName, true),
        defaultTimezone,
      },
      handleExportByStore,
      resetExportModal,
    );

    if (shouldRefreshModal) {
      refreshGenericModal(params);
      return;
    }

    openGenericModal(params);
  };

  const resetExportModal = () => {
    closeGenericModal();
    const activeStores = formattedStores.filter(({ active }) => active);
    const uniqGroups = getUniqueGroupsOfStores(activeStores);

    setExportGroups(uniqGroups);
    setExportSelectedGroups(uniqGroups);
  };

  const handleExportByStore = async () => {
    // Close export setup modal and open export progress modal
    resetExportModal();

    if (!exportSelectedStores || !exportSelectedStores.length) {
      return;
    }

    const exportSelectedStoreIds = exportSelectedStores.map(({ id }) => id);

    const dataToExport = await fetchAnalytics(
      exportSelectedStoreIds,
      exportPeriod.startDate,
      exportPeriod.endDate,
      METRICS,
      { withNonSalesConsumption },
    );

    props.openModalExportInfo({
      component: OrderAnalyticsByCategoryByStoreExportModal,
      title: i18next.t('ORDERS.BY_CATEGORY.EXPORT_IN_PROGRESS'),
      stores: exportSelectedStores,
      startDate: exportPeriod.startDate,
      endDate: exportPeriod.endDate,
      turnoverByStoreId: dataToExport.turnoverByStoreId,
      totalLossByStoreId: dataToExport.lossByStoreId,
      metrics: METRICS,
      currency,
      storeName,
      showMessage,
      withNonSalesConsumption,
    });
  };

  return (
    <Container>
      <NavigationBreadCrumb featurePath={path} />
      <ContentContainer>
        <HeaderContainer>
          <FilterContainer>
            <DeepsightFiltersButton
              advancedFilters={advancedFilters}
              applyFilters={applyFilters}
              brands={hasMultipleBrands ? clientBrands : null}
              columnsFilterList={columnsFilterList}
              customToggles={[customFilterWithNonSalesConsumption]}
              disableExport={isEmpty(activeData) || isEmpty(activeData[selectedMetric.key])}
              endDate={endDate}
              filters={filtersOrderAnalytics}
              focusedDateInput={focusedDateInput}
              formattedInventoryStockDay={formattedInventoryStockDay}
              groups={groups}
              handleExport={triggerExport}
              handleExportByStore={() => handleFoodCostByCategoryExportByStoreModal()}
              handleSelectedDates={handleSelectedDates}
              isLoading={isLoading || isRetrievingAnalytics}
              locations={locations}
              lossRate={lossRate}
              metrics={METRICS}
              readOnly={isLoading || isRetrievingAnalytics}
              retailers={clientRetailers}
              selectedBrands={selectedBrands}
              selectedGroups={selectedGroups}
              selectedLocations={selectedLocations}
              selectedMetric={selectedMetric}
              selectedRetailers={selectedRetailers}
              selectedStores={selectedStores}
              setAdvancedFilters={setAdvancedFilters}
              setApplyFilters={setApplyFilters}
              setEndMonth={setEndMonth}
              setFilters={setFiltersOrderAnalytics}
              setFocusedDateInput={setFocusedDateInput}
              setSelectedBrands={setSelectedBrands}
              setSelectedGroups={setSelectedGroups}
              setSelectedLocations={setSelectedLocations}
              setSelectedMetric={setSelectedMetric}
              setSelectedRetailers={setSelectedRetailers}
              setSelectedStores={setSelectedStores}
              setStartMonth={setStartMonth}
              startDate={startDate}
              stores={formattedStores}
              textFilterButton={i18next.t('GENERAL.ANALYTICS_BUTTON')}
              turnOver={turnOver}
            />
          </FilterContainer>
        </HeaderContainer>
        <ListContainer>
          {renderEmptyState && (
            <div style={{ verticalAlign: 'middle', height: 'calc(100vh - 185px)' }}>
              <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />
            </div>
          )}
          {!renderEmptyState && (
            <NestedList
              data={activeData[selectedMetric.key] || []}
              defaultOrderBy={ORDER_BY_KEY}
              defaultOrderType={'asc'}
              fixedRowsData={
                selectedMetric.key === 'turnover' ? [totalRow[selectedMetric.key]] : []
              }
              headers={headers}
              isLoading={isLoading || isRetrievingAnalytics}
              minWidth={'978px'}
              hasNestedData
              isExpandable
            />
          )}
        </ListContainer>
      </ContentContainer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  currency: state.baseReducer.currency,
  stores: getSalesPointStores(state.baseReducer.stores),
  client: getClientInfo(state.baseReducer.user),
  clientBrands: state.baseReducer.brands,
  clientRetailers: state.baseReducer.retailers,
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showMessage: (message, type) => {
    dispatch(showConfirmationMessage(message, type));
  },
  openModalExportInfo: (params) => {
    dispatch(openSmallModal(params));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(OrdersAnalyticsCategory);
